'use client'

import { lazy, Suspense, useEffect, type JSX } from 'react'
import { replace as replaceRoute } from '@/lib/router'
import { menus } from '@/lib/constants'
import type {
  Account,
  AccountProfile,
  Contact,
  ContactLabel,
  ConsoleUser,
  KeywordRule,
  LicenseRuntimeStatus,
  LocalWechatAccount,
  ScheduledTask,
  SettingsState,
} from '@/types'
import type { LoginImageCaptcha, LoginStartOptions } from '@/hooks/useLogin'

// Dashboard is the default landing view — keep it in the main chunk so first
// paint never waits on a second request. Everything else loads on demand.
import { DashboardView } from '@/views/DashboardView'

const AutoReplyView = lazy(() => import('@/views/message/AutoReplyView').then(m => ({ default: m.AutoReplyView })))
const ScheduledView = lazy(() => import('@/views/message/ScheduledView').then(m => ({ default: m.ScheduledView })))
const MassMessageView = lazy(() => import('@/views/message/MassMessageView').then(m => ({ default: m.MassMessageView })))
const RealtimeConversationView = lazy(() => import('@/views/conversation/RealtimeConversationView').then(m => ({ default: m.RealtimeConversationView })))
const AddressBookView = lazy(() => import('@/views/contact/AddressBookView').then(m => ({ default: m.AddressBookView })))
const ContactStatusView = lazy(() => import('@/views/contact/ContactStatusView').then(m => ({ default: m.ContactStatusView })))
const LabelsView = lazy(() => import('@/views/contact/LabelsView').then(m => ({ default: m.LabelsView })))
const GroupWelcomeView = lazy(() => import('@/views/group/GroupWelcomeView').then(m => ({ default: m.GroupWelcomeView })))
const GroupAutoReplyView = lazy(() => import('@/views/group/GroupAutoReplyView').then(m => ({ default: m.GroupAutoReplyView })))
const GroupMembersView = lazy(() => import('@/views/group/GroupMembersView').then(m => ({ default: m.GroupMembersView })))
const PersonalView = lazy(() => import('@/views/settings/PersonalView').then(m => ({ default: m.PersonalView })))
const ConsoleAccountView = lazy(() => import('@/views/settings/ConsoleAccountView').then(m => ({ default: m.ConsoleAccountView })))
const PartnerView = lazy(() => import('@/views/settings/PartnerView').then(m => ({ default: m.PartnerView })))
const ConsoleUsersView = lazy(() => import('@/views/settings/ConsoleUsersView').then(m => ({ default: m.ConsoleUsersView })))
const AnnouncementsView = lazy(() => import('@/views/settings/AnnouncementsView').then(m => ({ default: m.AnnouncementsView })))
const AnnouncementsAdminView = lazy(() => import('@/views/settings/AnnouncementsAdminView').then(m => ({ default: m.AnnouncementsAdminView })))
const AiConfigView = lazy(() => import('@/views/settings/AiConfigView').then(m => ({ default: m.AiConfigView })))
const AutomationView = lazy(() => import('@/views/settings/AutomationView').then(m => ({ default: m.AutomationView })))
const VoiceChangerView = lazy(() => import('@/views/settings/VoiceChangerView').then(m => ({ default: m.VoiceChangerView })))
const TTSConfigView = lazy(() => import('@/views/settings/TTSConfigView').then(m => ({ default: m.TTSConfigView })))
const WebhookSettingsView = lazy(() => import('@/views/settings/WebhookSettingsView').then(m => ({ default: m.WebhookSettingsView })))
const SystemSettingsView = lazy(() => import('@/views/settings/SystemSettingsView').then(m => ({ default: m.SystemSettingsView })))
const MomentsView = lazy(() => import('@/views/message/MomentsView').then(m => ({ default: m.MomentsView })))
const SupportView = lazy(() => import('@/views/settings/SupportView').then(m => ({ default: m.SupportView })))

// Menu filtering alone is cosmetic — deep links must also be blocked here.
const ADMIN_ONLY_ITEM_IDS = new Set(
  menus.settings.items.filter((item) => item.adminOnly).map((item) => item.id),
)

function AdminOnlyRedirect(): JSX.Element {
  useEffect(() => {
    replaceRoute('/dashboard')
  }, [])
  return <ViewLoading />
}

function ViewLoading(): JSX.Element {
  return (
    <div className="flex h-full min-h-[200px] items-center justify-center text-muted">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
    </div>
  )
}

type ViewRouterProps = {
  activeItemId: string
  account: Account | null
  contacts: Contact[]
  friends: Contact[]
  groups: Contact[]
  labels: ContactLabel[]
  rules: KeywordRule[]
  tasks: ScheduledTask[]
  settings: SettingsState
  accountProfile: AccountProfile | null
  localAccounts: LocalWechatAccount[]
  localAccountsError: string
  licenseStatus: LicenseRuntimeStatus | null
  refreshLicenseStatus: () => Promise<void> | void
  switchAccount: (account: LocalWechatAccount) => void
  removeWxAccount: (wxid: string) => Promise<void> | void
  onlineWxAccount: (wxid: string) => Promise<void> | void
  downlineWxAccount: (wxid: string) => Promise<void> | void
  startAddWechatAccount: () => void
  consoleUser: ConsoleUser | null
  onConsoleUserChange: (user: ConsoleUser) => void
  consoleLogout: () => Promise<void> | void
  announcementUnreadCount: number
  onAnnouncementUnreadCountChange: (count: number) => void
  syncing: boolean
  syncError: string
  profileRefreshing: boolean
  saveRule: (rule: Partial<KeywordRule>) => Promise<void> | void
  deleteRule: (id: number) => Promise<void> | void
  saveTask: (task: Partial<ScheduledTask>) => Promise<void> | void
  deleteTask: (id: number) => Promise<void> | void
  refreshTasks: () => Promise<void> | void
  saveSettings: (patch: Partial<SettingsState>) => Promise<void> | void
  syncContacts: (mode?: 'initial' | 'diff') => Promise<void> | void
  resolveContact: (wxid: string) => Promise<Contact | null>
  refreshProfile: () => Promise<void> | void
  logout: () => Promise<void> | void
  qrImage: string
  loginStatus: string
  secureVerifyUrl: string
  loginLoading: boolean
  captcha: LoginImageCaptcha | null
  captchaSubmitting: boolean
  onRefreshQr: (options: LoginStartOptions) => Promise<void> | void
  onSubmitCaptcha: (code: string) => Promise<void> | void
  onResetLogin: () => void
}

export function ViewRouter(props: ViewRouterProps): JSX.Element {
  return (
    <Suspense fallback={<ViewLoading />}>
      <ViewSwitch {...props} />
    </Suspense>
  )
}

function ViewSwitch(props: ViewRouterProps): JSX.Element {
  const {
    activeItemId,
    account,
    contacts,
    friends,
    groups,
    labels,
    rules,
    tasks,
    settings,
    accountProfile,
    localAccounts,
    localAccountsError,
    licenseStatus,
    refreshLicenseStatus,
    switchAccount,
    removeWxAccount,
    onlineWxAccount,
    downlineWxAccount,
    startAddWechatAccount,
    consoleUser,
    onConsoleUserChange,
    consoleLogout,
    announcementUnreadCount,
    onAnnouncementUnreadCountChange,
    syncing,
    syncError,
    profileRefreshing,
    saveRule,
    deleteRule,
    saveTask,
    deleteTask,
    refreshTasks,
    saveSettings,
    syncContacts,
    resolveContact,
    refreshProfile,
    logout,
    qrImage,
    loginStatus,
    secureVerifyUrl,
    loginLoading,
    captcha,
    captchaSubmitting,
    onRefreshQr,
    onSubmitCaptcha,
    onResetLogin,
  } = props

  if (ADMIN_ONLY_ITEM_IDS.has(activeItemId) && consoleUser?.role !== 'admin') {
    return <AdminOnlyRedirect />
  }

  switch (activeItemId) {
    case 'dashboard':
      return (
        <DashboardView
          account={account}
          consoleUser={consoleUser!}
          localAccounts={localAccounts}
          localAccountsError={localAccountsError}
          switchAccount={switchAccount}
          onlineWxAccount={onlineWxAccount}
          downlineWxAccount={downlineWxAccount}
          removeWxAccount={removeWxAccount}
          startAddWechatAccount={startAddWechatAccount}
        />
      )
    case 'realtime':
      return (
        <RealtimeConversationView
          account={account}
          contacts={contacts}
          resolveContact={resolveContact}
          settings={settings}
        />
      )
    case 'auto-reply':
      return (
        <AutoReplyView
          contacts={friends}
          labels={labels}
          settings={settings}
          rules={rules}
          saveSettings={saveSettings}
          saveRule={saveRule}
          deleteRule={deleteRule}
        />
      )
    case 'scheduled':
      return (
        <ScheduledView
          contacts={friends}
          labels={labels}
          tasks={tasks}
          onSaveTask={saveTask}
          onDeleteTask={deleteTask}
          onRefresh={refreshTasks}
          voiceId={settings.tts?.selectedVoiceId}
        />
      )
    case 'mass':
      return <MassMessageView account={account} contacts={friends} labels={labels} />
    case 'address-book':
      return (
        <AddressBookView
          contacts={friends}
          account={account}
          syncContacts={syncContacts}
          syncing={syncing}
          syncError={syncError}
        />
      )
    case 'check-status':
      return <ContactStatusView contacts={friends} account={account} />
    case 'labels':
      return <LabelsView account={account} contacts={friends} resolveContact={resolveContact} />
    case 'welcome':
      return <GroupWelcomeView groups={groups} settings={settings} saveSettings={saveSettings} />
    case 'group-auto-reply':
      return <GroupAutoReplyView groups={groups} settings={settings} saveSettings={saveSettings} />
    case 'members':
      return <GroupMembersView groups={groups} account={account} />
    case 'personal':
      return (
        <PersonalView
          account={account}
          profile={accountProfile}
          contacts={contacts}
          loginAccounts={localAccounts.filter((item) => !!consoleUser && item.localUserUuid === consoleUser.uuid)}
          logout={logout}
          refreshProfile={refreshProfile}
          profileRefreshing={profileRefreshing}
          qrImage={qrImage}
          loginStatus={loginStatus}
          secureVerifyUrl={secureVerifyUrl}
          loginLoading={loginLoading}
          captcha={captcha}
          captchaSubmitting={captchaSubmitting}
          onRefreshQr={onRefreshQr}
          onSubmitCaptcha={onSubmitCaptcha}
          onResetLogin={onResetLogin}
        />
      )
    case 'console-account':
      return (
        <ConsoleAccountView
          consoleUser={consoleUser}
          onConsoleUserChange={onConsoleUserChange}
          licenseStatus={licenseStatus}
          refreshLicenseStatus={refreshLicenseStatus}
          consoleLogout={consoleLogout}
        />
      )
    case 'console-users':
      return <ConsoleUsersView consoleUser={consoleUser} />
    case 'announcements':
      return (
        <AnnouncementsView
          unreadCount={announcementUnreadCount}
          onUnreadCountChange={onAnnouncementUnreadCountChange}
        />
      )
    case 'announcements-admin':
      return <AnnouncementsAdminView consoleUser={consoleUser} />
    case 'partner':
      return <PartnerView />
    case 'ai-config':
      return <AiConfigView settings={settings} saveSettings={saveSettings} />
    case 'automation':
      return <AutomationView settings={settings} saveSettings={saveSettings} />
    case 'system':
      return <SystemSettingsView settings={settings} saveSettings={saveSettings} />
    case 'voice-changer':
      return <VoiceChangerView settings={settings} saveSettings={saveSettings} />
    case 'tts-config':
      return <TTSConfigView settings={settings} saveSettings={saveSettings} />
    case 'webhook-trigger':
      return <WebhookSettingsView account={account} contacts={friends} groups={groups} labels={labels} />
    case 'moments':
      return <MomentsView account={account} contacts={friends} labels={labels} />
    case 'support':
      // 静态客服信息页，不依赖登录态数据。
      return <SupportView />
    default:
      return (
        <DashboardView
          account={account}
          consoleUser={consoleUser!}
          localAccounts={localAccounts}
          localAccountsError={localAccountsError}
          switchAccount={switchAccount}
          onlineWxAccount={onlineWxAccount}
          downlineWxAccount={downlineWxAccount}
          removeWxAccount={removeWxAccount}
          startAddWechatAccount={startAddWechatAccount}
        />
      )
  }
}
